import React, { useEffect, useState } from 'react'
import { InputSelect, InputSelectOption } from '@components/Form/InputSelect'
import { Button } from '@components/Basic/ButtonNbe'
import { ApiPlan } from 'BackendApi'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

interface Props {
  optionsMtu: InputSelectOption[]
  optionsBridge: InputSelectOption[]
  onSelectionChange: (matchedPlans: ApiPlan[]) => void
  onPlanSelect: (matchedPlans: ApiPlan[]) => void
  plans: ApiPlan[]
  isCurrentPlan: boolean
  userCurrentPlan?: ApiPlan
  isTrialAvailable: boolean
  userIsInTrial: boolean
  isYearlyChecked: boolean
}

const removeDuplicates = (
  arr: { value: string; label: string }[]
): { value: string; label: string }[] => {
  return [
    ...new Map(
      arr.map((item) => [JSON.stringify([item.value, item.label]), item])
    ).values(),
  ]
}

const sortOptionsByValue = (options: InputSelectOption[]) => {
  return [...options].sort(
    (a, b) => parseInt(a.value, 10) - parseInt(b.value, 10)
  )
}

export const ProControllers: React.FC<Props> = ({
  optionsMtu,
  optionsBridge,
  onPlanSelect,
  plans,
  onSelectionChange,
  userCurrentPlan,
  isCurrentPlan,
  isTrialAvailable,
  userIsInTrial,
  isYearlyChecked,
}) => {
  const { t } = useTranslation()

  const uniqOptionsMtu = sortOptionsByValue(removeDuplicates(optionsMtu))
  const uniqBridgesMtu = sortOptionsByValue(removeDuplicates(optionsBridge))

  const [currentMtu, setCurrentMtu] = useState(
    userCurrentPlan?.mtuLimit.toString() || ''
  )
  const [currentBridge, setCurrentBridge] = useState(
    userCurrentPlan?.bridgesLimit.toString() || ''
  )

  const [isCurrentYearlyPeriod] = useState(
    (userCurrentPlan?.period_unit.toString() || '') === 'year'
  )

  const selectionMatchesUserCurrentPlan =
    isCurrentPlan &&
    currentMtu === userCurrentPlan?.mtuLimit.toString() &&
    currentBridge === userCurrentPlan?.bridgesLimit.toString() &&
    isCurrentYearlyPeriod === isYearlyChecked

  const buttonText =
    selectionMatchesUserCurrentPlan && isCurrentPlan && userIsInTrial
      ? t('billing.pricing.ctaUpgradeNowSkipTrial')
      : selectionMatchesUserCurrentPlan
      ? t('billing.pricing.ctaCurrentPlan')
      : isTrialAvailable && userIsInTrial
      ? t('billing.pricing.ctaChangePlanButStillTrial')
      : isTrialAvailable
      ? t('billing.pricing.ctaStartTrial')
      : userCurrentPlan?.id.includes('pro') && !isCurrentPlan
      ? t('billing.pricing.ctaChoosePlan')
      : t('billing.pricing.ctaUpgradeNow')

  const updatePrice = (currentMtu: string, currentBridge: string) => {
    const matchedPlans = plans.filter(
      (plan) =>
        plan.mtuLimit === parseInt(currentMtu) &&
        plan.bridgesLimit === parseInt(currentBridge)
    )
    if (matchedPlans) {
      onSelectionChange(matchedPlans)
    }
  }

  const onMtuSelectChange = (newValue: string) => {
    setCurrentMtu(newValue)
  }

  const onBridgeSelectChange = (newValue: string) => {
    setCurrentBridge(newValue)
  }

  useEffect(() => {
    updatePrice(currentMtu, currentBridge)
  }, [currentMtu, currentBridge])

  useEffect(() => {
    // on mount if is not the current plan we need to select price for first options
    // (customer can have a pro plan not existing anymore)
    if (!isCurrentPlan || !userCurrentPlan) {
      const firstMtuLimit = optionsMtu[0].value
      const firstBridgeLimit = optionsBridge[0].value
      setCurrentBridge(firstBridgeLimit)
      setCurrentMtu(firstMtuLimit)
    }
  }, [isCurrentPlan, userCurrentPlan])

  const handlePlanSelect = () => {
    const matchedPlans = plans.filter(
      (plan) =>
        plan.mtuLimit === parseInt(currentMtu) &&
        plan.bridgesLimit === parseInt(currentBridge)
    )
    if (matchedPlans) {
      onPlanSelect(matchedPlans)
    }
  }

  return (
    <>
      <InputSelect
        value={currentMtu}
        onChange={(e) => onMtuSelectChange(e.target.value)}
        $hasMargin
        name={'ProPlanMtu'}
        $options={uniqOptionsMtu}
      />
      <InputSelect
        value={currentBridge}
        onChange={(e) => onBridgeSelectChange(e.target.value)}
        name={'ProPlanBridges'}
        $options={uniqBridgesMtu}
      />
      <Button
        onClick={handlePlanSelect}
        disabled={selectionMatchesUserCurrentPlan && !userIsInTrial}
        $fluid
        $size={'large'}
        $variant={
          selectionMatchesUserCurrentPlan && !userIsInTrial
            ? 'secondary'
            : 'primary'
        }
      >
        {buttonText}
      </Button>
    </>
  )
}
